import {
	faBell,
	faClipboardCheck,
	faFileLines,
	faHeadset,
	type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import {
	getNotificationsUnreadPoll,
	NotificationsPanel,
	NotificationsPanelButton,
	type NotificationsPanelPaletteModel,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	portalNotificationsSource,
	resolvePortalNotificationPath,
} from '../services/portalNotificationService';
import { useNotificationTrayStore } from '../stores/notificationTrayStore';

/** The storefront's notification settings page. */
export const PORTAL_NOTIFICATION_SETTINGS_PATH = '/account/notifications';

/**
 * The shared bell tray in the storefront's own colors: its brand surface,
 * border and muted text (set by BrandThemeApplier) instead of the CRM theme.
 */
const STOREFRONT_TRAY_PALETTE: Partial<NotificationsPanelPaletteModel> = {
	surface: 'var(--brand-surface, #ffffff)',
	border: 'var(--brand-border, #e5e7eb)',
	textSecondary: 'var(--brand-text-muted, #6b7280)',
	link: 'var(--color-portal-accent, #005EEB)',
	unreadDot: 'var(--color-portal-accent, #005EEB)',
	// The desktop bell sits on the brand-colored header.
	badgeRing: 'var(--brand-primary, #0058DB)',
};

function storefrontNotificationIcon(
	_type: string,
	category: string,
): IconDefinition {
	if (category === 'support') {
		return faHeadset;
	}
	// PD-16845 — an order approval request.
	if (category === 'orders') {
		return faClipboardCheck;
	}
	// PD-16863 — a new statement; the same document icon the menu uses for
	// Statements.
	if (category === 'billing') {
		return faFileLines;
	}
	return faBell;
}

/**
 * Desktop header bell (PD-16475): badge + tray over the signed-in customer's
 * notifications. The badge polls every 60s (30s while the tray is open);
 * there is no realtime connection on the storefront, see the report on
 * PD-16475. Render only for a signed-in customer.
 */
export function PortalNotificationBell(): JSX.Element {
	const navigate = useNavigate();
	return (
		<NotificationsPanelButton
			source={portalNotificationsSource}
			entityPathResolver={resolvePortalNotificationPath}
			iconResolver={storefrontNotificationIcon}
			settingsPath={PORTAL_NOTIFICATION_SETTINGS_PATH}
			settingsLabel="Notification settings"
			palette={STOREFRONT_TRAY_PALETTE}
			buttonClassName="relative flex items-center justify-center p-2 min-h-touch min-w-touch text-brand-primary-fg/85 hover:text-brand-primary-fg transition-colors"
			onNavigate={(path) => {
				void navigate(path);
			}}
		/>
	);
}

/**
 * The tray opened from the mobile menu drawer's Notifications row. Mounted
 * once in the layout, outside the drawer, because the drawer unmounts when it
 * closes and opening the tray closes it.
 */
export function PortalNotificationTray(): JSX.Element | null {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const isOpen = useNotificationTrayStore((s) => s.isOpen);
	const close = useNotificationTrayStore((s) => s.close);
	const refresh = getNotificationsUnreadPoll(portalNotificationsSource).refresh;

	// The bottom nav stays tappable above the tray; close it on any route
	// change so it never lingers over the page the customer moved to.
	useEffect(() => {
		close();
	}, [pathname, close]);

	return (
		<NotificationsPanel
			isOpen={isOpen}
			onClose={close}
			source={portalNotificationsSource}
			entityPathResolver={resolvePortalNotificationPath}
			iconResolver={storefrontNotificationIcon}
			settingsPath={PORTAL_NOTIFICATION_SETTINGS_PATH}
			settingsLabel="Notification settings"
			palette={STOREFRONT_TRAY_PALETTE}
			onChanged={() => {
				void refresh();
			}}
			onNavigate={(path) => {
				void navigate(path);
			}}
		/>
	);
}

/**
 * The signed-in customer's unread count, from the same shared poll as the
 * bell. Subscribes only while `enabled`, so a signed-out visitor never polls.
 */
export function usePortalUnreadCount(enabled: boolean): number {
	const poll = getNotificationsUnreadPoll(portalNotificationsSource);
	const [count, setCount] = useState(poll.getSnapshot);

	useEffect(() => {
		if (!enabled) {
			// Signed out by any path (idle timeout, expired session): drop the
			// last customer's count so the next sign-in starts from zero.
			poll.reset();
			return;
		}
		return poll.subscribe(setCount);
	}, [enabled, poll]);

	return enabled ? count : 0;
}
